import { useAtomValue } from "jotai";
import { useRef, useState } from "react";

import type { KnowledgeProduct } from "@go-novus/admin-api";
import {
  LedgerTable,
  Panel,
  ScrollArea,
  ProductLedgerRow,
  SearchField,
  SelectField,
  SourceRow,
} from "@go-novus/ui";

import {
  knowledgeProductsAtom,
  readKnowledgeFailureMessage,
} from "#/features/knowledge";
import * as m from "#/paraglide/messages";
import { getLocale } from "#/paraglide/runtime";
import { useDebouncedValue } from "#/shared/lib/debounced-value";
import { useFailureNotice } from "#/shared/ui/failure-notice";
import { InfiniteScroll } from "#/shared/ui/infinite-scroll";

import { KnowledgeNotice } from "./knowledge-notice";

/** 类目筛选器的「不筛」选项；契约里对应字段缺省。 */
const ALL = "all";

/** 价格按后端给的币种格式化；金额是字符串，不在前端做浮点运算。 */
function formatPrice(price: KnowledgeProduct["price"]): string {
  return new Intl.NumberFormat(getLocale(), {
    currency: price.currency,
    style: "currency",
  }).format(Number(price.amount));
}

/** Shopify 不跟踪库存时后端给 null，摆破折号而不是 0。 */
function formatInventory(inventory: number | null): string {
  return inventory === null
    ? m.knowledge_inventory_untracked()
    : new Intl.NumberFormat(getLocale()).format(inventory);
}

/** 占位行数：撑满一屏之内的高度，多了纯属让人等着看动画。 */
const SKELETON_ROWS = 5;

interface ProductCardProps {
  product: KnowledgeProduct;
}

/**
 * 窄屏下的一件商品。保留缩略图、标题和 handle，
 * 价格、库存、变体数降成一行次要信息。
 */
function ProductCard({ product }: ProductCardProps) {
  return (
    <div className="flex items-start gap-2.75 border-b border-[var(--border-surface-default)] px-4.5 py-4 last:border-b-0">
      {product.imageUrl !== null && (
        <img
          alt=""
          className="h-10.5 w-10.5 flex-none rounded-[var(--radius)] bg-[var(--bg-surface-raised)] object-cover"
          height="42"
          loading="lazy"
          src={product.imageUrl}
          width="42"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-title font-semibold">{product.title}</div>
        <div className="font-mono text-mono text-[var(--content-fg-subtle)]">
          {product.handle}
        </div>
        <div className="mt-1.5 text-micro text-[var(--content-fg-subtle)]">
          {m.knowledge_product_meta({
            category: product.category ?? m.knowledge_category_none(),
            inventory: formatInventory(product.inventory),
            price: formatPrice(product.price),
            variants: String(product.variantCount),
          })}
        </div>
      </div>
    </div>
  );
}

interface ProductTableProps {
  hasFilters: boolean;
  loading: boolean;
  loadingMore: boolean;
  products: readonly KnowledgeProduct[];
}

function ProductTable({
  hasFilters,
  loading,
  loadingMore,
  products,
}: ProductTableProps) {
  if (!loading && products.length === 0) {
    return (
      <KnowledgeNotice
        body={
          hasFilters
            ? m.knowledge_empty_search_body()
            : m.knowledge_empty_products_body()
        }
        heading={
          hasFilters
            ? m.knowledge_empty_search_heading()
            : m.knowledge_empty_products_heading()
        }
      />
    );
  }

  return (
    <>
      {loading && (
        <span className="sr-only" role="status">
          {m.knowledge_loading()}
        </span>
      )}
      {/* 内容区窄于 48rem 时使用卡片，避免表格横滚；两个分支渲染同一批数据。 */}
      <div className="@min-[48rem]:hidden">
        {!loading &&
          products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        {(loading || loadingMore) &&
          Array.from({ length: loading ? SKELETON_ROWS : 3 }, (_, row) => (
            <SourceRow key={row} loading />
          ))}
      </div>
      <div className="hidden @min-[48rem]:block">
        <LedgerTable
          stickyHeader
          columns={[
            { label: m.knowledge_column_product() },
            { label: m.knowledge_column_category() },
            { label: m.knowledge_column_price() },
            { label: m.knowledge_column_inventory() },
            { label: m.knowledge_column_variants() },
          ]}
        >
          {!loading &&
            products.map((product) => (
              <ProductLedgerRow
                category={product.category ?? m.knowledge_category_none()}
                handle={product.handle}
                image={product.imageUrl ?? undefined}
                inventory={formatInventory(product.inventory)}
                key={product.id}
                price={formatPrice(product.price)}
                title={product.title}
                variants={String(product.variantCount)}
              />
            ))}
          {(loading || loadingMore) &&
            Array.from({ length: loading ? SKELETON_ROWS : 3 }, (_, row) => (
              <ProductLedgerRow key={row} loading />
            ))}
        </LedgerTable>
      </div>
    </>
  );
}

/**
 * Products 标签：搜索与类目筛选在这里，不在页面上——另外四类没有这些查询参数，
 * 把筛选行提到页面就得为它们渲染一排用不上的控件。
 */
export function ProductSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(ALL);
  // 边打字边发请求的话一个词就是一串请求，停手 250ms 才进 query key。
  const settledQuery = useDebouncedValue(query.trim());
  const list = useAtomValue(
    knowledgeProductsAtom({
      category: category === ALL ? undefined : category,
      q: settledQuery === "" ? undefined : settledQuery,
    }),
  );
  const pages = list.data?.pages ?? [];
  const products = pages.flatMap((page) => page.items);
  const hasFilters = settledQuery !== "" || category !== ALL;
  // 类目表是本店全部类目，与当前筛选无关，任意一页带回来的都一样。
  const categories = pages[0]?.categories ?? [];
  const blockingFailure = useFailureNotice({
    failedAt: list.errorUpdatedAt,
    hasContent: list.data !== undefined,
    message: list.isError
      ? (readKnowledgeFailureMessage(list.error) ??
        m.knowledge_failure_unexpected())
      : null,
  });

  return (
    <>
      <div className="flex min-h-9 flex-wrap items-center gap-2.5">
        <SearchField
          label={m.knowledge_search_label()}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          placeholder={m.knowledge_search_placeholder()}
          value={query}
        />
        <div className="w-50 flex-none">
          <SelectField
            emptyMessage={m.knowledge_category_empty()}
            hideLabel
            label={m.knowledge_category_label()}
            onValueChange={setCategory}
            options={[
              { label: m.knowledge_category_all(), value: ALL },
              ...categories.map((item) => ({ label: item, value: item })),
            ]}
            searchPlaceholder={m.knowledge_category_search()}
            value={category}
          />
        </div>
      </div>
      {/* 卡片按内容长高，长到页面放不下为止，之后列表在卡片里滚。 */}
      <Panel className="flex min-h-0 flex-col">
        <ScrollArea height="auto" viewportRef={scrollRef}>
          {blockingFailure !== null ? (
            <KnowledgeNotice heading={blockingFailure} iconName="info" />
          ) : (
            <ProductTable
              hasFilters={hasFilters}
              loading={list.isPending}
              loadingMore={list.isFetchingNextPage}
              products={products}
            />
          )}
          <InfiniteScroll
            hasNextPage={list.hasNextPage}
            isFetchingNextPage={list.isFetchingNextPage}
            label={m.knowledge_load_more()}
            loadingLabel={m.knowledge_loading_more()}
            loadingContent={null}
            onLoadMore={() => {
              void list.fetchNextPage();
            }}
            rootRef={scrollRef}
          />
        </ScrollArea>
      </Panel>
    </>
  );
}
